import { useState, useRef, useEffect, useCallback } from "react";
import { api, formatApiError, CATEGORIES } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { motion } from "framer-motion";
import RecurrenceFields, { defaultRecurrence, buildSeriesPayload } from "./RecurrenceFields";
import {
  X, Microphone, Stop, Pause, Play, ArrowClockwise, CheckCircle, Warning,
  CloudArrowUp, TextT, Sparkle, UsersThree, ClipboardText, Spinner,
} from "@phosphor-icons/react";

const STAGES = [
  { key: "uploading", label: "Uploading recording", Icon: CloudArrowUp },
  { key: "transcribing", label: "Transcribing", Icon: TextT },
  { key: "organizing", label: "Organizing information", Icon: Sparkle },
  { key: "matching", label: "Matching employees and locations", Icon: UsersThree },
  { key: "preview", label: "Creating task preview", Icon: ClipboardText },
];

const inputCls = "w-full h-11 px-3 rounded-lg border-2 border-black bg-white outline-none focus:ring-2 focus:ring-primary text-sm";
const labelCls = "text-xs font-bold uppercase tracking-[0.12em] text-black/60 mb-1 block";

function confLevel(v) {
  if (v == null) return null;
  if (v >= 0.8) return { label: "High", color: "#DCFCE7" };
  if (v >= 0.5) return { label: "Medium", color: "#FEF9C3" };
  return { label: "Needs review", color: "#FEE2E2" };
}

function ConfBadge({ value, needsReview }) {
  const c = needsReview ? { label: "Needs review", color: "#FEE2E2" } : confLevel(value);
  if (!c) return null;
  return <span data-testid="conf-badge" className="text-[10px] font-bold px-2 py-0.5 rounded-full border-2 border-black" style={{ backgroundColor: c.color }}>{c.label}</span>;
}

function ItemStatusBadge({ status }) {
  if (status === "saved") return <span data-testid="item-status-saved" className="text-[10px] font-bold px-2 py-0.5 rounded-full border-2 border-black bg-[#DCFCE7]">Saved</span>;
  if (status === "error") return <span data-testid="item-status-error" className="text-[10px] font-bold px-2 py-0.5 rounded-full border-2 border-black bg-[#FEE2E2]">Needs fix</span>;
  if (status === "saving") return <span data-testid="item-status-saving" className="text-[10px] font-bold px-2 py-0.5 rounded-full border-2 border-black bg-[#FEF9C3]">Saving…</span>;
  return <span className="text-[10px] font-bold px-2 py-0.5 rounded-full border-2 border-black bg-white">Pending</span>;
}

const TYPE_LABEL = { task: "Task", reminder: "Reminder", calendar: "Calendar Event", message: "Message", meeting: "Meeting", update: "Update Task", complete: "Complete Task" };

const fmt = (s) => `${String(Math.floor(s / 60)).padStart(2, "0")}:${String(s % 60).padStart(2, "0")}`;

const isRecurringExt = (ext) => !!(ext?.recurrence && ext.recurrence.type && ext.recurrence.type !== "does_not_repeat");
const recSummary = (ext) => {
  const rec = ext?.recurrence;
  if (!isRecurringExt(ext)) return "";
  const days = (rec.days_of_week || []).map((d) => ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"][d]).join(", ");
  return [rec.type.replace(/_/g, " "), days && `on ${days}`, rec.time && `at ${rec.time}`, rec.end_date && `until ${rec.end_date}`].filter(Boolean).join(" ");
};
const needsField = (ext, field) => ext?.missing_information?.some((x) => (x || "").toLowerCase().includes(field));

function summarizeItem(it) {
  if (it.ext?.summary) return it.ext.summary;
  const f = it.form;
  switch (it.mode) {
    case "task": return `Task: ${f.title || "Untitled"}${f.due_date ? ` (due ${f.due_date})` : ""}`;
    case "reminder": return `Reminder: ${f.title || "Untitled"}${f.due_date ? ` on ${f.due_date}` : ""}`;
    case "calendar": return `Calendar: ${f.cal_title || "Event"}${f.cal_date ? ` on ${f.cal_date}` : ""}${f.cal_start ? ` at ${f.cal_start}` : ""}`;
    case "message": return `Message: ${(f.msg_body || "").slice(0, 60)}`;
    case "meeting": return `Meeting: ${f.meet_title || "Untitled"}${f.meet_date ? ` on ${f.meet_date}` : ""}`;
    case "update": return "Update an existing task";
    case "complete": return "Mark a task complete";
    default: return TYPE_LABEL[it.mode] || "Item";
  }
}

function deriveMode(intent, c) {
  const intentToMode = { create: "task", reminder: "reminder", calendar_event: "calendar", message: "message", meeting: "meeting", update: "update", complete: "complete" };
  const capOk = { task: true, reminder: true, calendar: !!c.calendar, message: !!c.message, meeting: !!c.meeting, update: true, complete: true };
  let mode = intentToMode[intent] || "task";
  if (!capOk[mode]) mode = "task";
  return mode;
}

function buildItemForm(e, m, meetingAttendees = []) {
  const ev = e.event || {}, msg = e.message || {}, mt = e.meeting || {};
  return {
    title: e.task_title || "", description: e.task_description || "",
    category: CATEGORIES.includes(cap(e.category)) ? cap(e.category) : "Urgent",
    subcategory: e.subcategory || "", priority: (e.priority || "normal").toLowerCase(),
    status: e.status || "not_started", start_date: e.start_date || "", due_date: e.due_date || "",
    due_time: e.due_time || "", estimated_duration_minutes: e.estimated_duration_minutes || "",
    tags: (e.tags || []).join(", "),
    auto_move_rule: (e.auto_move && e.auto_move !== "none") ? e.auto_move : "next_day",
    visibility: "private",
    assigned_employee_id: m.employee.selected_id || "",
    customer_id: m.customer.selected_id || "", create_customer: false, customer_name: e.customer_name || "",
    location_id: m.location.selected_id || "", create_location: false, location_name: e.location_name || "",
    vendor_id: m.vendor.selected_id || "", create_vendor: false, vendor_name: e.vendor_name || "",
    equipment_id: m.equipment.selected_id || "", create_equipment: false, equipment_name: e.equipment_name || "", equipment_type: e.equipment_type || "",
    reminders: (e.reminders || []).map((r) => ({ ...r })),
    cal_title: ev.title || e.task_title || "", cal_date: ev.date || e.due_date || "",
    cal_all_day: !!ev.all_day, cal_start: ev.start_time || "", cal_end: ev.end_time || "", cal_notes: ev.notes || "",
    msg_scope: msg.scope === "direct" ? "direct" : "broadcast", msg_recipient_id: "", msg_body: msg.body || "",
    meet_title: mt.title || e.task_title || "", meet_date: mt.date || e.due_date || "",
    meet_time: mt.time || e.due_time || "", meet_duration: mt.duration_minutes || 30,
    meet_attendees: meetingAttendees || [],
  };
}

export default function VoiceCapture({ open, onClose, onCreated }) {
  const { user } = useAuth();
  const isManager = ["owner", "store_manager", "assistant_manager"].includes(user?.role);
  const [stage, setStage] = useState("idle"); // idle | recording | processing | review | error
  const [recording, setRecording] = useState(false);
  const [paused, setPaused] = useState(false);
  const [seconds, setSeconds] = useState(0);
  const [procIdx, setProcIdx] = useState(0);
  const [errorMsg, setErrorMsg] = useState("");
  const [capId, setCapId] = useState(null);
  const [transcript, setTranscript] = useState("");
  const [items, setItems] = useState([]);
  const [bulkSaving, setBulkSaving] = useState(false);
  const [meta, setMeta] = useState({ employees: [], customers: [], locations: [], vendors: [], equipment: [] });
  const [caps, setCaps] = useState({ task: true, reminder: true, calendar: false, message: false, message_broadcast: false, message_direct: false, meeting: false });

  const mediaRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);
  const pressRef = useRef(0);
  const blobRef = useRef(null);

  const reset = useCallback(() => {
    setStage("idle"); setRecording(false); setPaused(false); setSeconds(0);
    setProcIdx(0); setErrorMsg(""); setCapId(null); setTranscript(""); setItems([]);
    setBulkSaving(false); blobRef.current = null; chunksRef.current = [];
    setCaps({ task: true, reminder: true, calendar: false, message: false, message_broadcast: false, message_direct: false, meeting: false });
    clearInterval(timerRef.current);
  }, []);

  useEffect(() => {
    if (open) { reset(); api.get("/voice-captures/meta").then((r) => setMeta(r.data)).catch(() => {}); }
    return () => clearInterval(timerRef.current);
  }, [open, reset]);

  if (!open) return null;

  const startTimer = () => { timerRef.current = setInterval(() => setSeconds((s) => s + 1), 1000); };

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mr = new MediaRecorder(stream);
      chunksRef.current = [];
      mr.ondataavailable = (e) => e.data.size > 0 && chunksRef.current.push(e.data);
      mr.onstop = () => {
        blobRef.current = new Blob(chunksRef.current, { type: "audio/webm" });
        stream.getTracks().forEach((t) => t.stop());
      };
      mr.start();
      mediaRef.current = mr;
      setRecording(true); setPaused(false); setSeconds(0); setStage("recording");
      startTimer();
    } catch (e) {
      setErrorMsg("Microphone permission denied. Please allow mic access, or type the update manually.");
      setStage("error");
    }
  };

  const finishRecording = () => {
    if (!mediaRef.current) return;
    clearInterval(timerRef.current);
    const secs = seconds;
    mediaRef.current.onstop = () => {
      blobRef.current = new Blob(chunksRef.current, { type: "audio/webm" });
      mediaRef.current.stream?.getTracks().forEach((t) => t.stop());
      if (secs < 1 || blobRef.current.size < 1000) {
        setErrorMsg("Recording too short. Please record again."); setStage("error"); return;
      }
      process(blobRef.current, secs);
    };
    mediaRef.current.stop();
    setRecording(false);
  };

  const cancelRecording = () => {
    clearInterval(timerRef.current);
    try { mediaRef.current?.stop(); mediaRef.current?.stream?.getTracks().forEach((t) => t.stop()); } catch {}
    reset();
  };

  const togglePause = () => {
    if (!mediaRef.current) return;
    if (paused) { mediaRef.current.resume(); startTimer(); setPaused(false); }
    else { mediaRef.current.pause(); clearInterval(timerRef.current); setPaused(true); }
  };

  // dual: tap to start/stop, hold to record
  const onPointerDown = () => {
    pressRef.current = Date.now();
    if (!recording) startRecording();
    else finishRecording();
  };
  const onPointerUp = () => {
    if (recording && Date.now() - pressRef.current >= 500) finishRecording();
  };

  const process = async (blob, secs) => {
    setStage("processing"); setProcIdx(0);
    try {
      const fd = new FormData();
      fd.append("file", new File([blob], "voice.webm", { type: "audio/webm" }));
      fd.append("duration_seconds", secs);
      const { data } = await api.post("/voice-captures", fd, { headers: { "Content-Type": "multipart/form-data" } });
      if (data.processing_status === "failed") {
        setErrorMsg(data.error_message || "Transcription failed."); setCapId(data.id); setTranscript(data.transcript || ""); setStage("error"); return;
      }
      setCapId(data.id); setTranscript(data.transcript);
      setProcIdx(2);
      await runExtract(data.id);
    } catch (e) {
      setErrorMsg(formatApiError(e.response?.data?.detail) || "Something went wrong. Your recording is saved — you can retry.");
      setStage("error");
    }
  };

  const runExtract = async (id) => {
    try {
      setProcIdx(3);
      const { data } = await api.post(`/voice-captures/${id}/extract`);
      setProcIdx(4);
      setTranscript(data.transcript);
      const c = data.capabilities || {};
      setCaps({ task: true, reminder: true, calendar: !!c.calendar, message: !!c.message,
                message_broadcast: !!c.message_broadcast, message_direct: !!c.message_direct, meeting: !!c.meeting });
      const built = (data.items || []).map((it) => {
        const intent = it.intent || "create";
        return {
          intent, mode: deriveMode(intent, c), ext: it.extraction, matches: it.matches,
          candidateTasks: it.candidate_tasks || [], form: buildItemForm(it.extraction, it.matches, it.meeting_attendee_ids || []),
          manualRec: defaultRecurrence(), selectedTaskId: "", status: "idle", error: "",
        };
      });
      if (built.length === 0) { setErrorMsg("Nothing usable was detected. Please try again."); setStage("error"); return; }
      setItems(built);
      setTimeout(() => setStage("review"), 400);
    } catch (e) {
      setErrorMsg(formatApiError(e.response?.data?.detail) || "AI extraction failed. Please retry."); setStage("error");
    }
  };

  const updateItem = (i, patch) => setItems((prev) => prev.map((it, idx) => (idx === i ? { ...it, ...patch } : it)));
  const setItemForm = (i, key, val) => setItems((prev) => prev.map((it, idx) => (idx === i ? { ...it, form: { ...it.form, [key]: val } } : it)));
  const setItemReminder = (i, ri, key, val) => setItems((prev) => prev.map((it, idx) => {
    if (idx !== i) return it;
    const rs = [...it.form.reminders]; rs[ri] = { ...rs[ri], [key]: val };
    return { ...it, form: { ...it.form, reminders: rs } };
  }));
  const toggleMeetAttendee = (i, empId) => setItems((prev) => prev.map((it, idx) => {
    if (idx !== i) return it;
    const attendees = it.form.meet_attendees.includes(empId) ? it.form.meet_attendees.filter((x) => x !== empId) : [...it.form.meet_attendees, empId];
    return { ...it, form: { ...it.form, meet_attendees: attendees } };
  }));

  const saveOneItem = async (i) => {
    const it = items[i];
    const { form, ext, matches, mode } = it;
    if (mode === "task") {
      if (!form.title.trim()) throw new Error("Task title is required");
      if (isRecurringExt(ext)) {
        const rr = ext.repeat_reminder || {}, rec = ext.recurrence;
        await api.post("/recurring/series", {
          title: form.title, description: form.description, assigned_employee_id: form.assigned_employee_id || null,
          category: form.category, priority: form.priority,
          recurrence_type: rec.type, recurrence_interval: rec.interval || 1,
          recurrence_days: rec.days_of_week || [], recurrence_day_of_month: rec.day_of_month || null,
          recurrence_months: rec.months || [],
          recurrence_rule: rec.nth_weekday ? { nth: rec.nth_weekday, weekday: (rec.days_of_week || [0])[0] } : {},
          start_date: rec.start_date || form.due_date || new Date().toISOString().slice(0, 10),
          default_time: rec.time || form.due_time || "08:00", end_date: rec.end_date || null,
          auto_move_rule: ext.auto_move && ext.auto_move !== "none" ? ext.auto_move : "none",
          reminder_rules: [
            { reminder_type: "at_due" },
            ...(rr.enabled ? [{ reminder_type: "at_due", repeat_until_complete: !!rr.until_complete, repeat_interval_minutes: rr.interval_minutes || 120 }] : []),
          ],
          voice_capture_id: capId,
        });
        return;
      }
      if (it.manualRec?.enabled) {
        if ((it.manualRec.type === "weekly" || it.manualRec.type === "biweekly") && it.manualRec.days.length === 0) throw new Error("Pick at least one day to repeat on");
        await api.post("/recurring/series", buildSeriesPayload({ ...form, voice_capture_id: capId }, it.manualRec));
        return;
      }
      await api.post(`/voice-captures/${capId}/approve`, {
        title: form.title, description: form.description, category: form.category, subcategory: form.subcategory,
        priority: form.priority, status: form.status, start_date: form.start_date || null, due_date: form.due_date || null,
        due_time: form.due_time || null,
        estimated_duration_minutes: form.estimated_duration_minutes ? parseInt(form.estimated_duration_minutes) : null,
        tags: form.tags.split(",").map((t) => t.trim()).filter(Boolean),
        auto_move_rule: form.auto_move_rule || "none",
        visibility: form.visibility || "private",
        assigned_employee_id: form.assigned_employee_id || null,
        customer_id: form.create_customer ? null : (form.customer_id || null), create_customer: form.create_customer, customer_name: form.customer_name,
        location_id: form.create_location ? null : (form.location_id || null), create_location: form.create_location, location_name: form.location_name,
        vendor_id: form.create_vendor ? null : (form.vendor_id || null), create_vendor: form.create_vendor, vendor_name: form.vendor_name,
        equipment_id: form.create_equipment ? null : (form.equipment_id || null), create_equipment: form.create_equipment, equipment_name: form.equipment_name, equipment_type: form.equipment_type,
        reminders: form.reminders.map((r) => ({ title: r.title, date: r.date, time: r.time || "08:00", reason: r.reason })),
      });
      return;
    }
    if (mode === "reminder") {
      if (!form.title.trim()) throw new Error("Reminder title is required");
      if (!form.due_date) throw new Error("Pick a date for the reminder");
      await api.post("/reminders", { title: form.title, reminder_date: form.due_date, reminder_time: form.due_time || "08:00", reason: form.description || "" });
      return;
    }
    if (mode === "calendar") {
      if (!form.cal_title.trim()) throw new Error("Event title is required");
      if (!form.cal_date) throw new Error("Pick a date for the event");
      await api.post("/calendar/events", {
        title: form.cal_title, date: form.cal_date, all_day: form.cal_all_day,
        start_time: form.cal_all_day ? null : (form.cal_start || null),
        end_time: form.cal_all_day ? null : (form.cal_end || null),
        notes: form.cal_notes || "", color: "#2563EB",
      });
      return;
    }
    if (mode === "message") {
      if (!form.msg_body.trim()) throw new Error("Message can't be empty");
      const scope = caps.message_broadcast ? form.msg_scope : "direct";
      const wantsDirectPick = scope === "direct" && caps.message_direct;
      if (wantsDirectPick && !form.msg_recipient_id) throw new Error("Choose who to message");
      await api.post("/comm/messages", { body: form.msg_body, scope, employee_id: wantsDirectPick ? form.msg_recipient_id : null });
      return;
    }
    if (mode === "meeting") {
      if (!form.meet_title.trim()) throw new Error("Meeting title is required");
      let scheduled_at = new Date().toISOString();
      if (form.meet_date) {
        const d = new Date(`${form.meet_date}T${form.meet_time || "09:00"}`);
        if (!isNaN(d.getTime())) scheduled_at = d.toISOString();
      }
      await api.post("/meetings", { title: form.meet_title, description: "", scheduled_at, duration_minutes: parseInt(form.meet_duration) || 30, attendee_ids: form.meet_attendees, ai_summary_enabled: true });
      return;
    }
    if (mode === "update" || mode === "complete") {
      if (!it.selectedTaskId) throw new Error("Select the task first");
      await api.post(`/voice-captures/${capId}/apply-update`, {
        task_id: it.selectedTaskId,
        status: mode === "complete" ? "completed" : (ext.status || null),
        due_date: ext.due_date || null, due_time: ext.due_time || null, note: ext.problem_description || null,
      });
      return;
    }
    throw new Error("Unknown item type");
  };

  const approveAll = async () => {
    setBulkSaving(true);
    let success = 0, fail = 0;
    for (let i = 0; i < items.length; i++) {
      if (items[i].status === "saved") { success++; continue; }
      updateItem(i, { status: "saving", error: "" });
      try {
        await saveOneItem(i);
        updateItem(i, { status: "saved", error: "" });
        success++;
      } catch (e) {
        const msg = e.response ? (formatApiError(e.response?.data?.detail) || "Failed to save") : (e.message || "Failed to save");
        updateItem(i, { status: "error", error: msg });
        fail++;
      }
    }
    setBulkSaving(false);
    if (fail === 0) {
      toast.success(items.length > 1 ? `All ${items.length} items saved` : "Saved");
      onCreated?.();
      onClose();
    } else {
      toast.error(`Saved ${success} of ${items.length} — fix the flagged item${fail > 1 ? "s" : ""} below and try again.`);
      if (success > 0) onCreated?.();
    }
  };

  return (
    <div className="fixed inset-0 z-[80] bg-black/50 flex items-end md:items-center justify-center" onClick={stage === "recording" ? undefined : onClose}>
      <div data-testid="voice-capture-modal" onClick={(e) => e.stopPropagation()} className="bg-white border-2 border-black md:rounded-xl rounded-t-2xl w-full max-w-lg max-h-[94vh] overflow-y-auto">
        <div className="sticky top-0 bg-secondary text-white border-b-2 border-black px-5 py-4 flex items-center justify-between z-10">
          <div className="flex items-center gap-2"><Microphone size={20} weight="fill" /><h2 className="font-display text-xl font-bold">Voice Capture</h2></div>
          <button data-testid="close-voice-btn" onClick={onClose} className="press w-9 h-9 rounded-full border-2 border-white/60 flex items-center justify-center"><X size={18} weight="bold" /></button>
        </div>

        {/* IDLE / RECORDING */}
        {(stage === "idle" || stage === "recording") && (
          <div className="p-8 flex flex-col items-center">
            {stage === "recording" && (
              <div className="mb-6 text-center">
                <div className="font-display text-5xl font-black tabular-nums" data-testid="rec-timer">{fmt(seconds)}</div>
                <p className="text-xs font-bold uppercase tracking-[0.2em] text-black/50 mt-1">{paused ? "Paused" : "Recording…"}</p>
              </div>
            )}
            <motion.button
              data-testid="mic-button"
              onPointerDown={onPointerDown} onPointerUp={onPointerUp}
              animate={recording && !paused ? { scale: [1, 1.08, 1] } : { scale: 1 }}
              transition={{ repeat: recording && !paused ? Infinity : 0, duration: 1.2 }}
              className={`w-36 h-36 rounded-full border-2 border-black hard-shadow flex items-center justify-center ${recording ? "bg-primary text-white" : "bg-[#E0F2FE]"}`}
            >
              {recording ? <Stop size={54} weight="fill" /> : <Microphone size={54} weight="fill" />}
            </motion.button>
            <p className="mt-5 font-bold text-lg">{recording ? "Tap to stop" : "Hold to Record"}</p>
            <p className="text-sm text-black/50">{recording ? "Or use Finish below" : "Tap once to begin, tap again to stop"}</p>
            {!recording && <p className="text-xs text-black/40 mt-2 max-w-xs text-center">Tip: you can mention more than one thing — e.g. "Tuesday and Thursday Liam has training at 7pm"</p>}

            {stage === "recording" && (
              <div className="flex gap-3 mt-6 w-full">
                <button data-testid="pause-btn" onClick={togglePause} className="press flex-1 h-11 rounded-full bg-[#FEF9C3] font-bold text-sm border-2 border-black flex items-center justify-center gap-1">{paused ? <><Play size={16} weight="fill" /> Resume</> : <><Pause size={16} weight="fill" /> Pause</>}</button>
                <button data-testid="cancel-rec-btn" onClick={cancelRecording} className="press flex-1 h-11 rounded-full bg-white font-bold text-sm border-2 border-black">Cancel</button>
                <button data-testid="finish-btn" onClick={finishRecording} className="press flex-1 h-11 rounded-full bg-primary text-white font-bold text-sm border-2 border-black">Finish</button>
              </div>
            )}
          </div>
        )}

        {/* PROCESSING */}
        {stage === "processing" && (
          <div className="p-8">
            <div className="space-y-3">
              {STAGES.map((s, i) => {
                const done = i < procIdx, active = i === procIdx;
                return (
                  <div key={s.key} data-testid={`proc-${s.key}`} className={`flex items-center gap-3 p-3 rounded-lg border-2 border-black ${done ? "bg-[#DCFCE7]" : active ? "bg-[#FEF9C3]" : "bg-white opacity-50"}`}>
                    <div className="w-9 h-9 rounded-full border-2 border-black flex items-center justify-center bg-white">
                      {done ? <CheckCircle size={18} weight="fill" /> : active ? <Spinner size={18} className="animate-spin" /> : <s.Icon size={18} weight="bold" />}
                    </div>
                    <span className="font-bold text-sm">{s.label}</span>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* ERROR */}
        {stage === "error" && (
          <div className="p-8 text-center">
            <div className="w-16 h-16 mx-auto rounded-full bg-[#FEE2E2] border-2 border-black flex items-center justify-center"><Warning size={30} weight="fill" color="#FF3B30" /></div>
            <p data-testid="voice-error" className="font-bold text-lg mt-4">{errorMsg}</p>
            <p className="text-sm text-black/50 mt-1">Your recording is not discarded.</p>
            <div className="flex flex-col gap-2 mt-6">
              {capId && transcript && <button data-testid="retry-extract-btn" onClick={() => { setStage("processing"); setProcIdx(3); runExtract(capId); }} className="press h-11 rounded-full bg-secondary text-white font-bold text-sm border-2 border-black">Retry Extraction</button>}
              <button data-testid="record-again-err-btn" onClick={reset} className="press h-11 rounded-full bg-primary text-white font-bold text-sm border-2 border-black flex items-center justify-center gap-2"><ArrowClockwise size={16} weight="bold" /> Record Again</button>
              <button data-testid="cancel-err-btn" onClick={onClose} className="press h-11 rounded-full bg-white font-bold text-sm border-2 border-black">Cancel</button>
            </div>
          </div>
        )}

        {/* REVIEW — list of one or more items */}
        {stage === "review" && items.length > 0 && (
          <div className="p-5 space-y-5">
            <div data-testid="smart-confirm-banner" className="bg-[#DCFCE7] border-2 border-black rounded-lg p-3">
              <p className="font-bold text-sm">
                {items.length > 1 ? `${items.length} items — ${items.map(summarizeItem).join("; ")}` : summarizeItem(items[0])} — does this look right?
              </p>
            </div>
            <div className="bg-[#FEF9C3] border-2 border-black rounded-lg p-3">
              <p className="text-sm text-black/60">Heard: <span className="italic">"{transcript}"</span></p>
              {items.length > 1 && <p data-testid="multi-item-banner" className="text-xs font-bold text-primary mt-1">{items.length} separate items detected — review each below, then Approve All</p>}
            </div>

            {items.map((it, i) => (
              <div key={i} data-testid={`item-${i}-card`} className={`border-2 border-black rounded-xl p-4 space-y-4 ${it.status === "saved" ? "bg-[#F7FEF9]" : it.status === "error" ? "bg-[#FFF5F5]" : "bg-white"}`}>
                <div className="flex items-center justify-between">
                  <p className="font-display font-bold text-sm">Item {i + 1} · {TYPE_LABEL[it.mode]}</p>
                  <ItemStatusBadge status={it.status} />
                </div>
                {it.error && <p data-testid={`item-${i}-error`} className="text-xs font-bold text-[#B91C1C]">{it.error}</p>}

                {(it.mode === "update" || it.mode === "complete") ? (
                  <>
                    {(it.ext.status || it.ext.due_date || it.ext.due_time) && it.mode !== "complete" && (
                      <div className="text-sm border-2 border-black rounded-lg p-3 bg-white">
                        <p className="text-[10px] font-bold uppercase tracking-wider text-black/40 mb-1">Proposed changes</p>
                        {it.ext.status && <p className="font-semibold">Status → {it.ext.status.replace(/_/g, " ")}</p>}
                        {it.ext.due_date && <p className="font-semibold">Due date → {it.ext.due_date}</p>}
                        {it.ext.due_time && <p className="font-semibold">Due time → {it.ext.due_time}</p>}
                      </div>
                    )}
                    <div>
                      <label className={labelCls}>Select the correct task ({it.candidateTasks.length})</label>
                      {it.candidateTasks.length === 0 && <p className="text-sm text-black/40">No matching tasks found.</p>}
                      <div className="space-y-2">
                        {it.candidateTasks.map((t) => (
                          <button key={t.id} data-testid={`item-${i}-candidate-${t.id}`} onClick={() => updateItem(i, { selectedTaskId: t.id })}
                            className={`press w-full text-left border-2 border-black rounded-lg p-3 bg-white ${it.selectedTaskId === t.id ? "ring-2 ring-primary" : ""}`}>
                            <div className="flex items-center gap-2">
                              <div className={`w-4 h-4 rounded-full border-2 border-black shrink-0 ${it.selectedTaskId === t.id ? "bg-primary" : "bg-white"}`} />
                              <p className="font-bold text-sm leading-tight">{t.title}</p>
                            </div>
                            <p className="text-xs text-black/50 mt-1 pl-6">{(t.status || "").replace(/_/g, " ")}{t.due_date ? ` · due ${t.due_date}` : ""}{t.assigned_to_name ? ` · ${t.assigned_to_name}` : ""}{t.customer_name ? ` · ${t.customer_name}` : ""}{t.equipment_name ? ` · ${t.equipment_name}` : ""}</p>
                          </button>
                        ))}
                      </div>
                    </div>
                  </>
                ) : (
                  <>
                    <div data-testid={`item-${i}-mode-toggle`} className="flex flex-wrap gap-1.5 p-1 rounded-2xl border-2 border-black bg-[#F3F4F6]">
                      {[
                        { key: "task", label: "Task", on: true },
                        { key: "reminder", label: "Reminder", on: true },
                        { key: "calendar", label: "Calendar", on: caps.calendar },
                        { key: "message", label: "Message", on: caps.message },
                        { key: "meeting", label: "Meeting", on: caps.meeting },
                      ].filter((m) => m.on).map((m) => (
                        <button key={m.key} data-testid={`item-${i}-mode-${m.key}-btn`} onClick={() => updateItem(i, { mode: m.key })}
                          className={`press h-9 px-4 rounded-full text-sm font-bold ${it.mode === m.key ? "bg-primary text-white" : "bg-transparent"}`}>{m.label}</button>
                      ))}
                    </div>

                    {it.mode === "task" && isRecurringExt(it.ext) && (
                      <div data-testid={`item-${i}-recurrence-banner`} className="bg-[#DCFCE7] border-2 border-black rounded-lg p-3 flex items-start gap-2">
                        <div className="w-8 h-8 rounded-full bg-white border-2 border-black flex items-center justify-center shrink-0"><ArrowClockwise size={16} weight="bold" /></div>
                        <div>
                          <p className="font-bold text-sm">Recurring task detected</p>
                          <p className="text-xs text-black/60 capitalize">{recSummary(it.ext)}</p>
                          {it.ext.auto_move && it.ext.auto_move !== "none" && <p className="text-[11px] text-black/50">Auto-move: {it.ext.auto_move.replace(/_/g, " ")}</p>}
                          {it.ext.repeat_reminder?.enabled && <p className="text-[11px] text-black/50">Repeat reminder every {it.ext.repeat_reminder.interval_minutes || 120} min{it.ext.repeat_reminder.until_complete ? " until complete" : ""}</p>}
                        </div>
                      </div>
                    )}

                    {(it.mode === "task" || it.mode === "reminder") && (<>
                      <div><label className={labelCls}>{it.mode === "reminder" ? "Reminder title" : "Task title"}</label><input data-testid={`item-${i}-title`} className={inputCls} value={it.form.title} onChange={(e) => setItemForm(i, "title", e.target.value)} /></div>
                      <div><label className={labelCls}>{it.mode === "reminder" ? "Details (optional)" : "Detailed description"}</label><textarea data-testid={`item-${i}-desc`} className="w-full min-h-[80px] p-3 rounded-lg border-2 border-black bg-white outline-none focus:ring-2 focus:ring-primary text-sm" value={it.form.description} onChange={(e) => setItemForm(i, "description", e.target.value)} /></div>
                    </>)}

                    {it.mode === "calendar" && (
                      <div data-testid={`item-${i}-calendar-fields`} className="space-y-4">
                        <div><label className={labelCls}>Event title</label><input data-testid={`item-${i}-cal-title`} className={inputCls} value={it.form.cal_title} onChange={(e) => setItemForm(i, "cal_title", e.target.value)} /></div>
                        <div className="grid grid-cols-2 gap-3">
                          <div><label className={labelCls}>Date</label><input data-testid={`item-${i}-cal-date`} type="date" className={inputCls} value={it.form.cal_date || ""} onChange={(e) => setItemForm(i, "cal_date", e.target.value)} /></div>
                          <label className="flex items-center gap-2 mt-6 font-bold text-sm"><input data-testid={`item-${i}-cal-allday`} type="checkbox" className="w-5 h-5" checked={it.form.cal_all_day} onChange={(e) => setItemForm(i, "cal_all_day", e.target.checked)} /> All day</label>
                        </div>
                        {!it.form.cal_all_day && (
                          <div className="grid grid-cols-2 gap-3">
                            <div><label className={labelCls}>Start</label><input data-testid={`item-${i}-cal-start`} type="time" className={inputCls} value={it.form.cal_start || ""} onChange={(e) => setItemForm(i, "cal_start", e.target.value)} /></div>
                            <div><label className={labelCls}>End</label><input data-testid={`item-${i}-cal-end`} type="time" className={inputCls} value={it.form.cal_end || ""} onChange={(e) => setItemForm(i, "cal_end", e.target.value)} /></div>
                          </div>
                        )}
                        <div><label className={labelCls}>Notes (optional)</label><textarea data-testid={`item-${i}-cal-notes`} className="w-full min-h-[70px] p-3 rounded-lg border-2 border-black bg-white outline-none focus:ring-2 focus:ring-primary text-sm" value={it.form.cal_notes} onChange={(e) => setItemForm(i, "cal_notes", e.target.value)} /></div>
                      </div>
                    )}

                    {it.mode === "message" && (
                      <div data-testid={`item-${i}-message-fields`} className="space-y-4">
                        {caps.message_broadcast && (
                          <div data-testid={`item-${i}-msg-scope`} className="grid grid-cols-2 gap-2 p-1 rounded-full border-2 border-black bg-[#F3F4F6]">
                            <button data-testid={`item-${i}-msg-scope-broadcast`} onClick={() => setItemForm(i, "msg_scope", "broadcast")} className={`press h-9 rounded-full text-sm font-bold ${it.form.msg_scope === "broadcast" ? "bg-primary text-white" : "bg-transparent"}`}>Announce to team</button>
                            <button data-testid={`item-${i}-msg-scope-direct`} onClick={() => setItemForm(i, "msg_scope", "direct")} className={`press h-9 rounded-full text-sm font-bold ${it.form.msg_scope === "direct" ? "bg-primary text-white" : "bg-transparent"}`}>Direct message</button>
                          </div>
                        )}
                        {caps.message_direct && it.form.msg_scope === "direct" && (
                          <div><label className={labelCls}>Send to</label>
                            <select data-testid={`item-${i}-msg-recipient`} className={inputCls} value={it.form.msg_recipient_id} onChange={(e) => setItemForm(i, "msg_recipient_id", e.target.value)}>
                              <option value="">Choose a person…</option>
                              {meta.employees.filter((u) => u.id !== user?.id).map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
                            </select>
                          </div>
                        )}
                        {!caps.message_broadcast && <p className="text-xs font-bold text-black/50">This note goes to store management.</p>}
                        <div><label className={labelCls}>Message</label><textarea data-testid={`item-${i}-msg-body`} className="w-full min-h-[100px] p-3 rounded-lg border-2 border-black bg-white outline-none focus:ring-2 focus:ring-primary text-sm" value={it.form.msg_body} onChange={(e) => setItemForm(i, "msg_body", e.target.value)} /></div>
                      </div>
                    )}

                    {it.mode === "meeting" && (
                      <div data-testid={`item-${i}-meeting-fields`} className="space-y-4">
                        <div><label className={labelCls}>Meeting title</label><input data-testid={`item-${i}-meet-title`} className={inputCls} value={it.form.meet_title} onChange={(e) => setItemForm(i, "meet_title", e.target.value)} /></div>
                        <div className="grid grid-cols-3 gap-3">
                          <div><label className={labelCls}>Date</label><input data-testid={`item-${i}-meet-date`} type="date" className={inputCls} value={it.form.meet_date || ""} onChange={(e) => setItemForm(i, "meet_date", e.target.value)} /></div>
                          <div><label className={labelCls}>Time</label><input data-testid={`item-${i}-meet-time`} type="time" className={inputCls} value={it.form.meet_time || ""} onChange={(e) => setItemForm(i, "meet_time", e.target.value)} /></div>
                          <div><label className={labelCls}>Minutes</label><input data-testid={`item-${i}-meet-duration`} type="number" min="5" step="5" className={inputCls} value={it.form.meet_duration} onChange={(e) => setItemForm(i, "meet_duration", e.target.value)} /></div>
                        </div>
                        <div><label className={labelCls}>Attendees</label>
                          <div className="grid grid-cols-2 gap-2 max-h-44 overflow-y-auto">
                            {meta.employees.filter((u) => u.id !== user?.id).map((u) => (
                              <button key={u.id} type="button" data-testid={`item-${i}-meet-attendee-${u.id}`} onClick={() => toggleMeetAttendee(i, u.id)}
                                className={`press text-left px-3 py-2 rounded-lg border-2 border-black text-sm font-bold ${it.form.meet_attendees.includes(u.id) ? "bg-primary text-white" : "bg-white"}`}>{u.name}</button>
                            ))}
                          </div>
                        </div>
                      </div>
                    )}

                    {it.mode === "reminder" && (
                      <div className="grid grid-cols-2 gap-3">
                        <div><label className={labelCls}>Remind me on</label><input data-testid={`item-${i}-rem-date`} type="date" className={inputCls} value={it.form.due_date || ""} onChange={(e) => setItemForm(i, "due_date", e.target.value)} /></div>
                        <div><label className={labelCls}>Time</label><input data-testid={`item-${i}-rem-time`} type="time" className={inputCls} value={it.form.due_time || ""} onChange={(e) => setItemForm(i, "due_time", e.target.value)} /></div>
                      </div>
                    )}

                    {it.mode === "task" && (<>
                      <RecordField label="Customer / Store" testid={`${i}-customer`} match={it.matches.customer} meta={meta.customers}
                        idKey="customer_id" createKey="create_customer" nameKey="customer_name" form={it.form} setF={(k, v) => setItemForm(i, k, v)}
                        conf={it.ext?.confidence?.customer_name} needsReview={needsField(it.ext, "customer")} />
                      <RecordField label="Location" testid={`${i}-location`} match={it.matches.location} meta={meta.locations}
                        idKey="location_id" createKey="create_location" nameKey="location_name" form={it.form} setF={(k, v) => setItemForm(i, k, v)}
                        conf={it.ext?.confidence?.location_name} needsReview={needsField(it.ext, "location")} />
                      <RecordField label="Equipment" testid={`${i}-equipment`} match={it.matches.equipment} meta={meta.equipment}
                        idKey="equipment_id" createKey="create_equipment" nameKey="equipment_name" form={it.form} setF={(k, v) => setItemForm(i, k, v)}
                        conf={it.ext?.confidence?.equipment_name} needsReview={needsField(it.ext, "equipment")} />

                      <div>
                        <div className="flex items-center gap-2 mb-1"><label className={labelCls + " mb-0"}>Assigned employee</label>
                          <ConfBadge value={it.ext?.confidence?.assigned_employee_name} needsReview={it.matches.employee.needs_selection} /></div>
                        <select data-testid={`item-${i}-employee`} className={inputCls + (it.matches.employee.needs_selection ? " ring-2 ring-primary" : "")} value={it.form.assigned_employee_id} onChange={(e) => setItemForm(i, "assigned_employee_id", e.target.value)}>
                          <option value="">{it.matches.employee.message || "Unassigned"}</option>
                          {(it.matches.employee.matches.length ? it.matches.employee.matches : meta.employees).map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
                        </select>
                        {it.matches.employee.message && <p className="text-xs text-primary font-bold mt-1">{it.matches.employee.message}</p>}
                      </div>

                      <RecordField label="Vendor" testid={`${i}-vendor`} match={it.matches.vendor} meta={meta.vendors}
                        idKey="vendor_id" createKey="create_vendor" nameKey="vendor_name" form={it.form} setF={(k, v) => setItemForm(i, k, v)}
                        conf={it.ext?.confidence?.vendor_name} needsReview={needsField(it.ext, "vendor")} />

                      <div className="grid grid-cols-2 gap-3">
                        <div><label className={labelCls}>Category</label><select data-testid={`item-${i}-category`} className={inputCls} value={it.form.category} onChange={(e) => setItemForm(i, "category", e.target.value)}>{CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}</select></div>
                        <div><label className={labelCls}>Priority</label><select data-testid={`item-${i}-priority`} className={inputCls} value={it.form.priority} onChange={(e) => setItemForm(i, "priority", e.target.value)}>{["low", "normal", "high", "urgent"].map((p) => <option key={p} value={p}>{p}</option>)}</select></div>
                      </div>
                      <div><label className={labelCls}>Status</label><select data-testid={`item-${i}-status`} className={inputCls} value={it.form.status} onChange={(e) => setItemForm(i, "status", e.target.value)}>{["not_started", "in_progress", "waiting_for_parts", "waiting_for_employee", "completed"].map((s) => <option key={s} value={s}>{s.replace(/_/g, " ")}</option>)}</select></div>

                      <div className="grid grid-cols-3 gap-3">
                        <div><label className={labelCls}>Start date</label><input data-testid={`item-${i}-start`} type="date" className={inputCls} value={it.form.start_date || ""} onChange={(e) => setItemForm(i, "start_date", e.target.value)} /></div>
                        <div><div className="flex items-center gap-1 mb-1"><label className={labelCls + " mb-0"}>Due date</label><ConfBadge value={it.ext?.confidence?.due_date} needsReview={needsField(it.ext, "due")} /></div><input data-testid={`item-${i}-due`} type="date" className={inputCls} value={it.form.due_date || ""} onChange={(e) => setItemForm(i, "due_date", e.target.value)} /></div>
                        <div><label className={labelCls}>Due time</label><input data-testid={`item-${i}-due-time`} type="time" className={inputCls} value={it.form.due_time || ""} onChange={(e) => setItemForm(i, "due_time", e.target.value)} /></div>
                      </div>
                      <div><label className={labelCls}>Estimated duration (min)</label><input data-testid={`item-${i}-duration`} type="number" className={inputCls} value={it.form.estimated_duration_minutes} onChange={(e) => setItemForm(i, "estimated_duration_minutes", e.target.value)} /></div>

                      <div><label className={labelCls}>Visibility</label>
                        <select data-testid={`item-${i}-visibility`} className={inputCls} value={it.form.visibility} onChange={(e) => setItemForm(i, "visibility", e.target.value)}>
                          <option value="private">Private — only me (creator)</option>
                          <option value="assigned">Assigned only — assignee & creator</option>
                          <option value="store">Shared — everyone in the store</option>
                          <option value="department">Shared — department</option>
                          <option value="company">Shared — company-wide (all stores)</option>
                        </select>
                      </div>

                      <label data-testid={`item-${i}-autorollover-toggle`} className="flex items-start gap-3 p-3 rounded-lg border-2 border-black bg-[#F3F4F6] cursor-pointer">
                        <input type="checkbox" className="w-5 h-5 mt-0.5 shrink-0" checked={it.form.auto_move_rule && it.form.auto_move_rule !== "none"} onChange={(e) => setItemForm(i, "auto_move_rule", e.target.checked ? "next_day" : "none")} />
                        <span>
                          <span className="font-bold text-sm block">Auto roll over to the next day</span>
                          <span className="text-xs text-black/50">If this task isn't completed by its due date, it automatically moves to the next day.</span>
                          {it.form.auto_move_rule && it.form.auto_move_rule !== "none" && (
                            <select data-testid={`item-${i}-autorollover-mode`} className="mt-2 h-9 px-2 rounded-lg border-2 border-black bg-white text-xs font-bold" value={it.form.auto_move_rule} onChange={(e) => setItemForm(i, "auto_move_rule", e.target.value)} onClick={(ev) => ev.stopPropagation()}>
                              <option value="next_day">Every next day</option>
                              <option value="next_weekday">Next working day (skip weekends)</option>
                            </select>
                          )}
                        </span>
                      </label>
                      {isManager && !isRecurringExt(it.ext) && (
                        <RecurrenceFields value={it.manualRec} onChange={(v) => updateItem(i, { manualRec: v })} testidPrefix={`item-${i}-rec`} />
                      )}
                      <div><label className={labelCls}>Tags (comma separated)</label><input data-testid={`item-${i}-tags`} className={inputCls} value={it.form.tags} onChange={(e) => setItemForm(i, "tags", e.target.value)} /></div>

                      <div>
                        <label className={labelCls}>Reminders</label>
                        {it.form.reminders.length === 0 && <p className="text-sm text-black/40">No reminders extracted.</p>}
                        {it.form.reminders.map((r, ri) => (
                          <div key={ri} data-testid={`item-${i}-reminder-${ri}`} className="border-2 border-black rounded-lg p-3 mb-2 bg-[#F9F9F6] space-y-2">
                            <input className={inputCls} value={r.title} placeholder="Reminder title" onChange={(e) => setItemReminder(i, ri, "title", e.target.value)} />
                            <div className="grid grid-cols-2 gap-2">
                              <input type="date" className={inputCls} value={r.date || ""} onChange={(e) => setItemReminder(i, ri, "date", e.target.value)} />
                              <input type="time" className={inputCls} value={r.time || "08:00"} onChange={(e) => setItemReminder(i, ri, "time", e.target.value)} />
                            </div>
                            {r.time_defaulted && <p className="text-[11px] font-bold text-black/50">⏰ No time was stated — defaulted to 08:00 local time.</p>}
                          </div>
                        ))}
                      </div>
                    </>)}
                  </>
                )}
              </div>
            ))}

            {/* Transcript */}
            <div><label className={labelCls}>Transcript</label><div data-testid="rv-transcript" className="bg-[#F9F9F6] border-2 border-black rounded-lg p-3 text-sm">{transcript}</div></div>

            {/* Actions */}
            <div className="sticky bottom-0 -mx-5 px-5 py-4 bg-white border-t-2 border-black space-y-2">
              <button data-testid="approve-all-btn" onClick={approveAll} disabled={bulkSaving} className="press w-full h-12 rounded-full bg-primary text-white font-bold border-2 border-black disabled:opacity-60">
                {bulkSaving ? "Saving…" : items.length > 1 ? `Approve All (${items.length})` : "Approve & Save"}
              </button>
              <div className="flex gap-2">
                <button data-testid="record-again-btn" onClick={reset} className="press flex-1 h-10 rounded-full bg-white font-bold text-sm border-2 border-black flex items-center justify-center gap-1"><ArrowClockwise size={14} weight="bold" /> Record Again</button>
                <button data-testid="cancel-review-btn" onClick={onClose} className="press flex-1 h-10 rounded-full bg-white font-bold text-sm border-2 border-black">Cancel</button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function cap(s) { return s ? s.charAt(0).toUpperCase() + s.slice(1) : s; }

function RecordField({ label, testid, match, meta, idKey, createKey, nameKey, form, setF, conf, needsReview }) {
  const suggestCreate = match?.suggest_create && form[nameKey];
  return (
    <div>
      <div className="flex items-center gap-2 mb-1"><label className={labelCls + " mb-0"}>{label}</label><ConfBadge value={conf} needsReview={needsReview} /></div>
      {form[createKey] ? (
        <div className="flex gap-2">
          <input data-testid={`rv-${testid}-newname`} className={inputCls} value={form[nameKey]} onChange={(e) => setF(nameKey, e.target.value)} placeholder={`New ${label.toLowerCase()} name`} />
          <button data-testid={`rv-${testid}-cancelnew`} onClick={() => setF(createKey, false)} className="press h-11 px-3 rounded-lg bg-white font-bold text-xs border-2 border-black shrink-0">Use existing</button>
        </div>
      ) : (
        <div className="flex gap-2">
          <select data-testid={`rv-${testid}`} className={inputCls} value={form[idKey]} onChange={(e) => setF(idKey, e.target.value)}>
            <option value="">{suggestCreate ? `Not found: "${form[nameKey]}"` : "None"}</option>
            {(meta || []).map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
          </select>
          {suggestCreate && <button data-testid={`rv-${testid}-create`} onClick={() => setF(createKey, true)} className="press h-11 px-3 rounded-lg bg-[#FEF9C3] font-bold text-xs border-2 border-black shrink-0">+ Create</button>}
        </div>
      )}
    </div>
  );
}
